import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Shield,
  Search,
  ChevronDown,
  Loader2,
  FilePlus,
  Send,
  Eye,
  CheckCircle,
  CheckCircle2,
  XCircle,
  Ban,
  Bell,
  RefreshCw,
  Trash2,
  Calendar,
} from "lucide-react";
import { esignApi } from "../../services/api";

const ACTION_CONFIG: Record<
  string,
  { icon: React.FC<any>; color: string; bg: string; label: string }
> = {
  created: {
    icon: FilePlus,
    color: "text-slate-500",
    bg: "bg-slate-100",
    label: "Request Created",
  },
  sent: {
    icon: Send,
    color: "text-blue-500",
    bg: "bg-blue-50",
    label: "Request Sent",
  },
  viewed: {
    icon: Eye,
    color: "text-amber-500",
    bg: "bg-amber-50",
    label: "Document Viewed",
  },
  signed: {
    icon: CheckCircle,
    color: "text-emerald-500",
    bg: "bg-emerald-50",
    label: "Signer Signed",
  },
  declined: {
    icon: XCircle,
    color: "text-red-500",
    bg: "bg-red-50",
    label: "Signer Declined",
  },
  voided: {
    icon: Ban,
    color: "text-red-500",
    bg: "bg-red-50",
    label: "Request Voided",
  },
  reminder_sent: {
    icon: Bell,
    color: "text-purple-500",
    bg: "bg-purple-50",
    label: "Reminder Sent",
  },
  completed: {
    icon: CheckCircle2,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    label: "Request Completed",
  },
  resent: {
    icon: RefreshCw,
    color: "text-blue-500",
    bg: "bg-blue-50",
    label: "Request Resent",
  },
  deleted: {
    icon: Trash2,
    color: "text-slate-500",
    bg: "bg-slate-100",
    label: "Request Deleted",
  },
};

const ACTION_FILTER_OPTIONS = [
  { value: "", label: "All Actions" },
  { value: "created", label: "Created" },
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
  { value: "signed", label: "Signed" },
  { value: "declined", label: "Declined" },
  { value: "voided", label: "Voided" },
  { value: "reminder_sent", label: "Reminder Sent" },
  { value: "completed", label: "Completed" },
];

function formatDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatAction(action: string): string {
  const config = ACTION_CONFIG[action];
  if (config) return config.label;
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

interface AuditEvent {
  action: string;
  at: string;
  actor?: string;
  ip?: string;
  userAgent?: string;
  envelopeId: string;
  envelopeSubject: string;
}

const EsignAuditLogTab: React.FC = () => {
  const navigate = useNavigate();

  const [events, setEvents] = useState<AuditEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [actionDropdownOpen, setActionDropdownOpen] = useState(false);

  const actionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchAuditEvents();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (actionRef.current && !actionRef.current.contains(e.target as Node)) {
        setActionDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchAuditEvents = async () => {
    setLoading(true);
    try {
      const res = await esignApi.list({ limit: 50 });
      const envelopes = res.data.data || res.data || [];
      const allEvents: AuditEvent[] = envelopes.flatMap((env: any) =>
        (env.auditTrail || []).map((entry: any) => ({
          ...entry,
          envelopeId: env._id,
          envelopeSubject: env.subject || "Untitled Request",
        }))
      );
      allEvents.sort(
        (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime()
      );
      setEvents(allEvents);
    } catch (err) {
      console.error("Failed to fetch audit events:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters
  const filteredEvents = events.filter((event) => {
    // Search filter
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      if (!event.envelopeSubject.toLowerCase().includes(q)) return false;
    }
    // Action filter
    if (actionFilter && event.action !== actionFilter) return false;
    // Date range filter
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      if (new Date(event.at) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (new Date(event.at) > to) return false;
    }
    return true;
  });

  const selectedActionLabel =
    ACTION_FILTER_OPTIONS.find((o) => o.value === actionFilter)?.label || "All Actions";

  return (
    <div>
      {/* Filter Bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
        {/* Search */}
        <div className="relative flex-1 w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by subject..."
            className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>

        {/* Action Type Filter */}
        <div className="relative" ref={actionRef}>
          <button
            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
            className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors whitespace-nowrap"
          >
            {selectedActionLabel}
            <ChevronDown
              className={`h-4 w-4 text-slate-400 transition-transform ${
                actionDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>
          {actionDropdownOpen && (
            <div className="absolute right-0 mt-1 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-20 py-1 overflow-hidden">
              {ACTION_FILTER_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => {
                    setActionFilter(opt.value);
                    setActionDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                    actionFilter === opt.value
                      ? "bg-emerald-50 text-emerald-700 font-medium"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Date From */}
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="pl-10 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 transition-colors"
            title="From date"
          />
        </div>

        {/* Date To */}
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="pl-10 pr-3 py-2.5 text-sm rounded-xl border border-slate-200 focus:outline-none focus:border-emerald-600 transition-colors"
            title="To date"
          />
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 text-slate-300 animate-spin" />
        </div>
      ) : filteredEvents.length === 0 ? (
        /* Empty State */
        <div className="flex flex-col items-center justify-center py-20">
          <div className="p-4 bg-slate-100 rounded-2xl mb-4">
            <Shield className="h-16 w-16 text-slate-300" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            No audit events yet
          </h3>
          <p className="text-sm text-slate-500 text-center max-w-sm">
            Activity will appear here as signing requests are created and signed
          </p>
        </div>
      ) : (
        /* Timeline */
        <div className="bg-white rounded-xl shadow-sm border border-slate-200">
          <div className="divide-y divide-slate-100">
            {filteredEvents.map((entry, i) => {
              const config = ACTION_CONFIG[entry.action] || ACTION_CONFIG.created;
              const ActionIcon = config.icon;

              return (
                <motion.div
                  key={`${entry.envelopeId}-${entry.at}-${i}`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.5) }}
                  className="flex items-start gap-4 px-5 py-4"
                >
                  <div className={`p-2 rounded-full flex-shrink-0 ${config.bg}`}>
                    <ActionIcon className={`h-4 w-4 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-slate-800">
                        {formatAction(entry.action)}
                      </p>
                      <span className="text-xs text-slate-400 whitespace-nowrap flex-shrink-0">
                        {formatDateTime(entry.at)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Request:{" "}
                      <span
                        className="font-medium text-slate-700 cursor-pointer hover:underline"
                        onClick={() => navigate(`/esign/${entry.envelopeId}`)}
                      >
                        {entry.envelopeSubject}
                      </span>
                    </p>
                    {entry.actor && (
                      <p className="text-xs text-slate-400 mt-0.5">
                        Actor: {entry.actor}
                        {entry.ip && ` \u2022 IP: ${entry.ip}`}
                      </p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default EsignAuditLogTab;
